/**
 * useBuddyBackend — fetches Buddy's current state and recent events from the backend.
 * This is the SOLE source of truth for Buddy's state. Frontend is read-only.
 *
 * Optimizations:
 * - ETag / If-None-Match → skips JSON parse on 304
 * - Visibility-aware polling: 45s foreground, 300s hidden, refresh on return
 * - Core/extras split: news fetched separately on 10min cadence
 * - Stable state reference to prevent unnecessary re-renders
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { BuddyEvent } from '@/lib/types/events';
import { LongTermMoodScores } from '@/lib/types/mood';
import { EnvironmentState } from '@/lib/state/environmentMoodComposer';
import { BuddyMacroState } from '@/lib/state/macroAdvisorEngine';

/** Shallow compare key scalar fields that drive UI re-renders */
function stateEqual(a: BackendBuddyState | null, b: BackendBuddyState | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.buddyMood === b.buddyMood
    && a.networkState === b.networkState
    && a.blockState === b.blockState
    && a.priceState === b.priceState
    && a.weather === b.weather
    && a.heatLevel === b.heatLevel
    && a.verdict === b.verdict
    && a.dayArchetype === b.dayArchetype
    && a.dailySummary === b.dailySummary
    && a.weeklyVibe === b.weeklyVibe
    && a.macroMode === b.macroMode
    && a.sentimentBucket === b.sentimentBucket
    && a.sentimentValue === b.sentimentValue
    && a.activeSignature === b.activeSignature
    && a.seasonState === b.seasonState
    && a.isStale === b.isStale
    && a.updatedAt === b.updatedAt
    && a.moodLabel === b.moodLabel
    && a.plainSubtitle === b.plainSubtitle
    && a.macroExplanation === b.macroExplanation
    && a.whatItMeans === b.whatItMeans
    && a.footerLine === b.footerLine
    && a.displayMacroState === b.displayMacroState;
}

export interface NewsItem {
  title: string;
  source: string;
  url: string | null;
  pubDate: number;
}

export interface BackendBuddyState {
  // Short-term
  networkState: string;
  blockState: string;
  priceState: string;
  buddyMood: string;
  // Weather
  weather: string;
  weatherDescription: string;
  // Heat
  heatLevel: number;
  // Copy layers
  verdict: string;
  dayArchetype: string;
  dailySummary: string;
  weeklyVibe: string;
  // Macro/sentiment
  macroMode: string;
  sentimentValue: number | null;
  sentimentBucket: string | null;
  // Long-term
  longTermScores: LongTermMoodScores;
  networkMood: string;
  marketMood: string;
  blockMood: string;
  ltConfidence: string;
  ltStatusLabel: string;
  // Environment (ambient visuals)
  environment: EnvironmentState;
  // Signature
  activeSignature: string | null;
  // Season (50WMA)
  seasonState: string;
  seasonDeviationPct: number;
  seasonIntensity: number;
  // Macro advisor (full scoring from backend)
  macroAdvisor: BuddyMacroState | null;
  // New macro display copy
  moodLabel: string | null;
  plainSubtitle: string | null;
  macroExplanation: string | null;
  whatItMeans: string | null;
  footerLine: string | null;
  displayMacroState: string | null;
  // News
  newsItems: NewsItem[];
  // Meta
  updatedAt: string;
  isStale: boolean;
}

export interface BackendMeta {
  snapshotCount: number;
  workerStatus: string;
  staleSec: number;
  cold: boolean;
}

export interface BuddyBackendOutput {
  state: BackendBuddyState | null;
  events: BuddyEvent[];
  meta: BackendMeta;
  loading: boolean;
  error: boolean;
  lastFetch: number;
}

const DEFAULT_META: BackendMeta = {
  snapshotCount: 0,
  workerStatus: 'unknown',
  staleSec: -1,
  cold: true,
};

const DEFAULT_ENVIRONMENT: EnvironmentState = {
  particleDensity: 0.15,
  particleSpeed: 0.2,
  particleOpacity: 0.25,
  ambientBrightness: 0.5,
  ambientWarmth: 0.5,
  glowIntensity: 0.15,
  spaciousness: 0.8,
  tensionLevel: 0.05,
  stillness: 0.8,
  bgGradient: 'radial-gradient(ellipse at 50% 50%, hsl(30 95% 56% / 0.02) 0%, transparent 65%)',
  pulseFrequency: 6,
};

const DEFAULT_SCORES: LongTermMoodScores = {
  sustained_calm: 0.5,
  sustained_congestion: 0,
  sustained_bullish: 0,
  sustained_caution: 0,
  sustained_crab: 0.3,
  sustained_block_pressure: 0,
  sustained_smooth_flow: 0.2,
};

// ── Polling intervals ──
const POLL_FOREGROUND = 45_000;   // 45s when visible
const POLL_BACKGROUND = 300_000;  // 5min when hidden tab
const NEWS_POLL_INTERVAL = 600_000; // 10min for news

function buildUrl(projectId: string, extras?: boolean): string {
  const base = `https://${projectId}.supabase.co/functions/v1/buddy-state`;
  if (extras) return `${base}?scope=extras`;
  return `${base}?events=5`;
}

function parseEnvironment(s: Record<string, unknown>): EnvironmentState {
  if (!s || typeof s !== 'object') return DEFAULT_ENVIRONMENT;
  return {
    particleDensity: (s.particleDensity as number) ?? DEFAULT_ENVIRONMENT.particleDensity,
    particleSpeed: (s.particleSpeed as number) ?? DEFAULT_ENVIRONMENT.particleSpeed,
    particleOpacity: (s.particleOpacity as number) ?? DEFAULT_ENVIRONMENT.particleOpacity,
    ambientBrightness: (s.ambientBrightness as number) ?? DEFAULT_ENVIRONMENT.ambientBrightness,
    ambientWarmth: (s.ambientWarmth as number) ?? DEFAULT_ENVIRONMENT.ambientWarmth,
    glowIntensity: (s.glowIntensity as number) ?? DEFAULT_ENVIRONMENT.glowIntensity,
    spaciousness: (s.spaciousness as number) ?? DEFAULT_ENVIRONMENT.spaciousness,
    tensionLevel: (s.tensionLevel as number) ?? DEFAULT_ENVIRONMENT.tensionLevel,
    stillness: (s.stillness as number) ?? DEFAULT_ENVIRONMENT.stillness,
    bgGradient: (s.bgGradient as string) ?? DEFAULT_ENVIRONMENT.bgGradient,
    pulseFrequency: (s.pulseFrequency as number) ?? DEFAULT_ENVIRONMENT.pulseFrequency,
  };
}

function parseScores(s: Record<string, unknown>): LongTermMoodScores {
  if (!s || typeof s !== 'object') return DEFAULT_SCORES;
  return {
    sustained_calm: (s.sustained_calm as number) ?? 0,
    sustained_congestion: (s.sustained_congestion as number) ?? 0,
    sustained_bullish: (s.sustained_bullish as number) ?? 0,
    sustained_caution: (s.sustained_caution as number) ?? 0,
    sustained_crab: (s.sustained_crab as number) ?? 0,
    sustained_block_pressure: (s.sustained_block_pressure as number) ?? 0,
    sustained_smooth_flow: (s.sustained_smooth_flow as number) ?? 0,
  };
}

function parseState(s: Record<string, unknown>): BackendBuddyState {
  return {
    networkState: s.networkState as string,
    blockState: s.blockState as string,
    priceState: s.priceState as string,
    buddyMood: s.buddyMood as string,
    weather: s.weather as string,
    weatherDescription: s.weatherDescription as string,
    heatLevel: Number(s.heatLevel),
    verdict: s.verdict as string,
    dayArchetype: s.dayArchetype as string,
    dailySummary: s.dailySummary as string,
    weeklyVibe: s.weeklyVibe as string,
    macroMode: s.macroMode as string,
    sentimentValue: s.sentimentValue as number | null,
    sentimentBucket: s.sentimentBucket as string | null,
    longTermScores: parseScores(s.longTermScores as Record<string, unknown>),
    networkMood: (s.networkMood as string) ?? 'SUSTAINED_NEUTRAL',
    marketMood: (s.marketMood as string) ?? 'SUSTAINED_NEUTRAL',
    blockMood: (s.blockMood as string) ?? 'SUSTAINED_NEUTRAL',
    ltConfidence: s.ltConfidence as string,
    ltStatusLabel: s.ltStatusLabel as string,
    environment: parseEnvironment(s.environment as Record<string, unknown>),
    activeSignature: s.activeSignature as string | null,
    seasonState: (s.seasonState as string) ?? 'neutral',
    seasonDeviationPct: Number(s.seasonDeviationPct ?? 0),
    seasonIntensity: Number(s.seasonIntensity ?? 0),
    macroAdvisor: (s.macroAdvisor as BuddyMacroState) ?? null,
    moodLabel: (s.moodLabel as string) ?? null,
    plainSubtitle: (s.plainSubtitle as string) ?? null,
    macroExplanation: (s.macroExplanation as string) ?? null,
    whatItMeans: (s.whatItMeans as string) ?? null,
    footerLine: (s.footerLine as string) ?? null,
    displayMacroState: (s.displayMacroState as string) ?? null,
    // News starts empty — filled by extras fetch
    newsItems: Array.isArray(s.newsItems) ? s.newsItems as NewsItem[] : [],
    updatedAt: s.updatedAt as string,
    isStale: s.isStale as boolean,
  };
}

export function useBuddyBackend(): BuddyBackendOutput {
  const [state, setState] = useState<BackendBuddyState | null>(null);
  const [events, setEvents] = useState<BuddyEvent[]>([]);
  const [meta, setMeta] = useState<BackendMeta>(DEFAULT_META);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [lastFetch, setLastFetch] = useState(0);
  const mountedRef = useRef(true);
  const etagRef = useRef<string | null>(null);
  const coreIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const newsIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const newsEtagRef = useRef<string | null>(null);

  const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID ?? 'kpomlqozspvmleljtcxp';
  const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

  // ── Core state fetch (lean, no news) ──
  const fetchCore = useCallback(async () => {
    try {
      const url = buildUrl(projectId);
      const headers: Record<string, string> = {
        'Authorization': `Bearer ${anonKey}`,
        'Content-Type': 'application/json',
      };
      if (etagRef.current) headers['If-None-Match'] = etagRef.current;

      const res = await fetch(url, { headers });
      if (!mountedRef.current) return;

      if (res.status === 304) {
        setError(false);
        setLastFetch(Date.now());
        if (loading) setLoading(false);
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const newETag = res.headers.get('etag');
      if (newETag) etagRef.current = newETag;

      const data = await res.json();
      if (!mountedRef.current) return;
      if (!data?.ok) { setError(true); return; }

      const newState = parseState(data.state);

      // Preserve existing news from extras (core doesn't include news)
      setState(prev => {
        if (prev?.newsItems?.length && !newState.newsItems?.length) {
          newState.newsItems = prev.newsItems;
        }
        return stateEqual(prev, newState) ? prev : newState;
      });

      setEvents(data.events ?? []);
      setMeta(data.meta ?? DEFAULT_META);
      setError(false);
      setLastFetch(Date.now());
    } catch {
      if (mountedRef.current) setError(true);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [projectId, anonKey]);

  // ── Extras fetch (news, slow cadence) ──
  const fetchExtras = useCallback(async () => {
    try {
      const url = buildUrl(projectId, true);
      const headers: Record<string, string> = {
        'Authorization': `Bearer ${anonKey}`,
        'Content-Type': 'application/json',
      };
      if (newsEtagRef.current) headers['If-None-Match'] = newsEtagRef.current;

      const res = await fetch(url, { headers });
      if (!mountedRef.current) return;
      if (res.status === 304) return; // News unchanged
      if (!res.ok) return;

      const newETag = res.headers.get('etag');
      if (newETag) newsEtagRef.current = newETag;

      const data = await res.json();
      if (!mountedRef.current || !data?.ok) return;

      const newsItems = Array.isArray(data.state?.newsItems) ? data.state.newsItems as NewsItem[] : [];
      if (newsItems.length > 0) {
        setState(prev => {
          if (!prev) return prev;
          return { ...prev, newsItems };
        });
      }
    } catch {
      // News fetch failure is non-critical
    }
  }, [projectId, anonKey]);

  // ── Visibility-aware polling ──
  useEffect(() => {
    mountedRef.current = true;

    // Initial fetches
    fetchCore();
    fetchExtras();

    const startCorePolling = (interval: number) => {
      if (coreIntervalRef.current) clearInterval(coreIntervalRef.current);
      coreIntervalRef.current = setInterval(fetchCore, interval);
    };

    const startNewsPolling = () => {
      if (newsIntervalRef.current) clearInterval(newsIntervalRef.current);
      newsIntervalRef.current = setInterval(fetchExtras, NEWS_POLL_INTERVAL);
    };

    // Start with appropriate interval
    startCorePolling(document.hidden ? POLL_BACKGROUND : POLL_FOREGROUND);
    startNewsPolling();

    const handleVisibility = () => {
      if (document.hidden) {
        // Tab hidden → slow core poll, pause news
        startCorePolling(POLL_BACKGROUND);
        if (newsIntervalRef.current) {
          clearInterval(newsIntervalRef.current);
          newsIntervalRef.current = null;
        }
      } else {
        // Tab visible → immediate refresh + fast poll
        fetchCore();
        startCorePolling(POLL_FOREGROUND);
        fetchExtras();
        startNewsPolling();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      mountedRef.current = false;
      if (coreIntervalRef.current) clearInterval(coreIntervalRef.current);
      if (newsIntervalRef.current) clearInterval(newsIntervalRef.current);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [fetchCore, fetchExtras]);

  return { state, events, meta, loading, error, lastFetch };
}
